import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { constants } from "@reactivated/constants";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconCircleClose from "../../../../img/icons/circle-close.svg";
import { ConfiguratorPrice } from "../../../components/ConfiguratorPrice";
import { Link } from "../../../components/Link";
import { RatingGraphic } from "../../../components/RatingGraphic";
import { RichText } from "../../../components/RichText";
import { IProduct } from "../../../models/catalogue";
import { DiscountDisplayMode } from "../../../models/discountDisplay";
import { WebPageURL, isoWebPageURL } from "../../../models/nominals";
import {
    ICompareTile,
    ITileID,
    isoTileID,
} from "../../../models/product-compare";
import { usePageSetting } from "../../../utils/settings";
import { getVariantPrice } from "../selectors";

const AppSlug = constants["tsicommon.types.AppSlug"];

type IProps = Immutable<{
    data: ICompareTile;
    selectedVariant: IProduct | null;
    priceSelectValue: string;
}> & {
    onRemoveButtonClick: (tileID: ITileID) => void;
};

const removeFromURLParams = (tileID: ITileID): void => {
    const window = getWindow();
    if (!window) return;

    const history = window.history;
    const urlParams = new URLSearchParams(window.location.search);
    const selectedTileIDs = decodeURIComponent(urlParams.get("tiles") || "")
        .split(",")
        .map(Number)
        .map(isoTileID.wrap);
    const newTileIDs = selectedTileIDs.filter((id) => id !== tileID);

    if (newTileIDs.length > 0) {
        urlParams.set("tiles", newTileIDs.join(","));
        const newURL = `${window.location.origin}${
            window.location.pathname
        }?${urlParams.toString()}`;
        history.replaceState(history.state, "", newURL);
    } else {
        urlParams.delete("tiles");
        const newURL = `${window.location.origin}${
            window.location.pathname
        }?${urlParams.toString()}`;
        history.replaceState(history.state, "", newURL);
    }
};

const buildPrice = (
    selectedVariant: IProduct | null,
    priceSelectValue: string,
): React.ReactNode => {
    if (selectedVariant) {
        return (
            <ConfiguratorPrice
                price={getVariantPrice(selectedVariant)}
                variant={selectedVariant}
                selectedAddonPrice={null}
                financingLink={isoWebPageURL.wrap("")}
                strikeThroughMSRP={false}
                discountDisplayMode={DiscountDisplayMode.DISABLED}
                actualPriceStyle={""}
            />
        );
    }
    return (
        <div className="configurator__price">
            <p>Unavailable in size {priceSelectValue}</p>
        </div>
    );
};

const buildRating = (
    selectedVariant: IProduct | null,
    variantURL: WebPageURL | null,
    appSlug: string | null,
): React.ReactNode => {
    const isCanada = appSlug === AppSlug.TEMPURCA;
    if (!selectedVariant || isCanada) {
        return null;
    }
    return (
        <a href={`${variantURL}#reviews__body`} title={t`Read Reviews`}>
            <RatingGraphic
                cardClass="tile"
                cardSize="large"
                numReviews={selectedVariant.num_reviews}
                rating={selectedVariant.rating}
            />
        </a>
    );
};

export const SelectedProduct = (props: IProps): React.ReactNode => {
    const handleRemoveButtonClick = () => {
        props.onRemoveButtonClick(props.data.product_compare_tile.id);
        removeFromURLParams(props.data.product_compare_tile.id);
    };
    const appSlug = usePageSetting("app_slug") || "";
    const variantURL = props.selectedVariant?.link || null;

    return (
        <div role="group" aria-label="selected product for comparison">
            <button
                type="button"
                className="al-comparetray-xoutproduct"
                onClick={handleRemoveButtonClick}
            >
                <SVG
                    className="basket-savings__title-icon"
                    aria-hidden="true"
                    src={iconCircleClose}
                />
                <span className="ada-screenreader-only">
                    {t`Remove product from compare`}
                </span>
            </button>
            <Link href={variantURL}>
                <RichText html={props.data.product_compare_tile.title} />
            </Link>
            {buildRating(props.selectedVariant, variantURL, appSlug)}
            {buildPrice(props.selectedVariant, props.priceSelectValue)}
        </div>
    );
};
